import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { radarCorreoApi, type AlertStatus } from "../services/radarCorreoService";

// Llaves de React Query por compañía: cambiar de compañía no mezcla datos.
const key = (companyId: string | null | undefined, ...rest: unknown[]) => ["radarCorreo", companyId || "none", ...rest];

function useCompany() {
  return useCompaniesStore((s) => s.companyId);
}

export function useRadarStatus() {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "status"), queryFn: radarCorreoApi.status, staleTime: 5 * 60 * 1000 });
}

/** Conexiones; mientras alguna esté leyendo se refresca cada 15 s. */
export function useConnections() {
  const companyId = useCompany();
  return useQuery({
    queryKey: key(companyId, "connections"),
    queryFn: radarCorreoApi.connections,
    refetchInterval: (q) => {
      const list = q.state.data || [];
      return list.some((c) => c.status === "active" && !c.backfill?.done) ? 15000 : false;
    },
  });
}

export function useJobs(connectionId: string | null, enabled = true) {
  const companyId = useCompany();
  return useQuery({
    queryKey: key(companyId, "jobs", connectionId),
    queryFn: () => radarCorreoApi.jobs(connectionId as string),
    enabled: Boolean(connectionId) && enabled,
    refetchInterval: (q) => ((q.state.data || []).some((j) => ["queued", "running", "waiting_batch"].includes(j.status)) ? 10000 : false),
  });
}

export function useOperations(params: { status?: string; stage?: string } = {}) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "operations", params), queryFn: () => radarCorreoApi.operations(params) });
}

export function useOperation(id: string | null) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "operation", id), queryFn: () => radarCorreoApi.operation(id as string), enabled: Boolean(id) });
}

export function useEvidence(id: string | null, enabled: boolean) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "evidence", id), queryFn: () => radarCorreoApi.evidence(id as string), enabled: Boolean(id) && enabled });
}

export function useCounterparties() {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "counterparties"), queryFn: radarCorreoApi.counterparties });
}

export function useAlerts(status?: AlertStatus, enabled = true) {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "alerts", status || "all"), queryFn: () => radarCorreoApi.alerts(status), enabled });
}

export function useFinance() {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "finance"), queryFn: radarCorreoApi.finance });
}

export function useSnapshot() {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "snapshot"), queryFn: radarCorreoApi.snapshot });
}

export function useSettings() {
  const companyId = useCompany();
  return useQuery({ queryKey: key(companyId, "settings"), queryFn: radarCorreoApi.settings });
}

/** Mutación que, al terminar, invalida todo lo del Radar de la compañía activa. */
export function useRadarMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  const companyId = useCompany();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: ["radarCorreo", companyId || "none"] }),
  });
}
